import React, { useState, useEffect, useCallback } from "react";

// --- CSS --- //
import "../../styles/gym-card.css";

// --- ASSETS --- //
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LinkIcon from "@mui/icons-material/Link";
import DirectionsIcon from "@mui/icons-material/Directions";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const GymCard = ({
    gymName,
    gymAddress,
    gymPhone,
    gymEmail,
    gymWebsite,
    gymHours,
}) => {
    const [isTimeContainerOpen, setIsTimeContainerOpen] = useState(false);
    const [isCopied, setIsCopied] = useState(false);
    const [status, setStatus] = useState({ text: "CLOSED", color: "#d9534f" });

    // Toggle Time Container Visibility
    const toggleTimeContainer = () => {
        setIsTimeContainerOpen(!isTimeContainerOpen);
    };

    // Copy Address to Clipboard
    const copyToClipboard = () => {
        navigator.clipboard
            .writeText(gymAddress)
            .then(() => {
                setIsCopied(true);
                setTimeout(() => setIsCopied(false), 2000);
            })
            .catch((error) => console.error("Failed to copy text: ", error));
    };

    // Function to parse time string into hours and minutes
    const parseTime = (timeStr) => {    
        const regex = /(\d+):?(\d*)(AM|PM)/i;
        const match = timeStr.match(regex);
    
        if (!match) {
            console.error("Invalid time format:", timeStr);
            return { hours: NaN, minutes: NaN }; // Return NaN for easier debugging
        }
        
        const [, hourStr, minuteStr, modifier] = match;
        let hours = Number(hourStr);
        let minutes = minuteStr ? Number(minuteStr) : 0;
        
        if (modifier.toUpperCase() === "PM" && hours < 12) hours += 12;
        if (modifier.toUpperCase() === "AM" && hours === 12) hours = 0;
    
        return { hours, minutes };
    };

    // Function to determine OPEN, CLOSING SOON, or CLOSED
    const calculateStatus = useCallback(() => {
        const today = new Date();
        const currentDayIndex = today.getDay();
        const currentHours = today.getHours();
        const currentMinutes = today.getMinutes();

        const todayHours = gymHours[currentDayIndex];

        if (!todayHours || todayHours.toLowerCase() === "closed") {
            setStatus({ text: "CLOSED", color: "#d9534f" });
            return;
        }

        const [openTimeStr, closeTimeStr] = todayHours.split(" - ").map((t) => t.trim());
        const { hours: openH, minutes: openM } = parseTime(openTimeStr);
        const { hours: closeH, minutes: closeM } = parseTime(closeTimeStr);

        let nowInMinutes = currentHours * 60 + currentMinutes;
        let openInMinutes = openH * 60 + openM;
        let closeInMinutes = closeH * 60 + closeM;

        // Handle overnight hours
        if (closeInMinutes < openInMinutes) {
            closeInMinutes += 24 * 60;
            if (nowInMinutes < openInMinutes) nowInMinutes += 24 * 60;
        }

        if (nowInMinutes < openInMinutes) {
            setStatus({ text: "CLOSED", color: "var(--alert-red)" });
        } else if (nowInMinutes >= openInMinutes && nowInMinutes < closeInMinutes - 30) {
            setStatus({ text: "OPEN", color: "var(--alert-green)" });
        } else if (nowInMinutes >= closeInMinutes - 30 && nowInMinutes < closeInMinutes) {
            setStatus({ text: "CLOSING", color: "var(--alert-yellow)" });
        } else {
            setStatus({ text: "CLOSED", color: "var(--alert-red)" });
        }
    }, [gymHours]);

    useEffect(() => {
        calculateStatus();
        const timer = setInterval(calculateStatus, 5000);
        return () => clearInterval(timer);
    }, [gymHours, calculateStatus]); // Now calculateStatus is memoized

    return (
        <div className="gym-card-container">
            <div className="gym-card-upper-container">
                <div className="text-container">
                    <h4 className="title">{gymName}</h4>
                    <p className="address">{gymAddress}</p>
                </div>
                <button id="copy-icon" onClick={copyToClipboard}>
                    {isCopied ? <CheckCircleIcon style={{ color: "var(--alert-green)" }} /> : <ContentCopyIcon />}
                </button>
            </div>

            <div className="gym-card-action-container">
                <div className="action-button-container">
                    <button className="action-button" onClick={() => (window.location.href = `mailto:${gymEmail}`)}>
                        <EmailIcon />
                    </button>
                    <button className="action-button" onClick={() => (window.location.href = `tel:${gymPhone}`)}>
                        <PhoneIcon />
                    </button>
                    <button className="action-button" onClick={() => window.open(gymWebsite, "_blank")}>
                        <LinkIcon />
                    </button>
                    <button className="action-button" onClick={() => window.open(`https://www.google.com/maps?q=${gymAddress}`, "_blank")}>
                        <DirectionsIcon />
                    </button>
                </div>

                <div
                    className="time-status-container"
                    onClick={toggleTimeContainer}
                    style={{ backgroundColor: status.color }}
                >
                    <button id="time-status">
                        {isTimeContainerOpen ? (
                            <ExpandLessIcon />
                        ) : (
                            <ExpandMoreIcon />
                        )}{status.text}
                    </button>
                </div>
            </div>

            {isTimeContainerOpen && (
                <div className="gym-card-time-container">
                    <h4 className="time-title">Hours of Operation</h4>
                    {gymHours.map((hours, index) => (
                        <div key={index} className="time-container">
                            <label>{["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][index]}:</label>
                            <p>{hours || "Closed"}</p>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default GymCard;